import { useAuraSupportInfo } from "src/Utils/Hooks/UseAuraInfoContext";
import AuraAppLicenses from "./AuraAppLicenses/AuraAppLicenses";
import { IAuraSoftwareSuiteLicensesInfo, IPartner, IUserLicencesInfo } from "src/Utils/Hooks/useGetAdminInfo/useGetAuraSupportInfo";
import { useGetAuraSupportInfoWrapper } from "src/Utils/Hooks/useGetAdminInfo/useGetAuraSupportInfoWrapper";
import { useMemo, useState } from "react";
import SearchBar from "src/SharedComponents/SearchBar/SearchBar";
import { useSearch } from "src/Utils/Hooks/SearchData/useSearch";
import AuraSoftwareSuiteLicenses from "./AuraSoftwareSuite/AuraSoftwareSuiteLicenses";
import DemoTrialLicense from "./DemoTrialLicense/DemoTrialLicense";

const Licenses: React.FC = () => {
    const { adminDetails } = useAuraSupportInfo();
    const { getAuraSupportInfo } = useGetAuraSupportInfoWrapper();
    const partnerDetails: IPartner[] | undefined = adminDetails?.partners;
    const [searchText, setSearchText] = useState<string>('');

    // Memoize search keys to ensure stable references
    const searchKeys1 = useMemo<(keyof IUserLicencesInfo)[]>(() => ['entitlementId', 'distributorName', 'clinicName' , 'licenseType'], []);
    const searchKeys2 = useMemo<(keyof IAuraSoftwareSuiteLicensesInfo)[]>(() => ['entitlementId', 'distributorName', 'clinicName', 'licenseType'], []);

    // Memoize usersAndStorageLicensesInfo and userLicensesInfo
    const usersAndStorageLicensesInfo = useMemo(() => adminDetails?.licenses?.usersAndStorageLicensesInfo || [], [adminDetails]);
    const userLicensesInfo = useMemo(() => adminDetails?.licenses?.userLicencesInfo || [], [adminDetails]);

    const isSuiteLicense = (licenseType: string) => licenseType.toLowerCase().includes("suite");

    // Memoize filtered Not Suite records
    const otherLicensesInfo = useMemo(() => {
        return usersAndStorageLicensesInfo.filter(
            (license) => !isSuiteLicense(license.licenseType)
        );
    }, [usersAndStorageLicensesInfo]);

    // Memoize remaining licenses
    const suiteLicensesInfo = useMemo(() => {
        return usersAndStorageLicensesInfo.filter(
            (license) => isSuiteLicense(license.licenseType)
        );
    }, [usersAndStorageLicensesInfo]);

    // Combine userLicensesInfo with unique Not Suite records
    const nonSuiteLicensesInfo = useMemo(() => {
        return [
            ...userLicensesInfo,
            ...otherLicensesInfo
        ];
    }, [userLicensesInfo, otherLicensesInfo]);

    // Apply search after processing
    const filteredUserLicenses = useSearch(nonSuiteLicensesInfo, searchText, searchKeys1);
    const filteredAuraSuiteLicenses = useSearch(suiteLicensesInfo, searchText, searchKeys2);

    return (
        <>
            <div className="row pb-3">
                <div className="col d-flex align-items-center w-100">
                    <SearchBar onChange={(text: string) => { setSearchText(text); }} />
                </div>
                <div className="col-auto d-flex justify-content-end">
                    <DemoTrialLicense 
                        updateLicenses={getAuraSupportInfo}/>
                </div>
            </div>
            {adminDetails && 
                <>
                    <div>
                        <AuraSoftwareSuiteLicenses 
                            auraSoftwareSuiteLicensesLicenses={filteredAuraSuiteLicenses} 
                            auraTotalSoftwareSuiteLicensesLicensesCount= {suiteLicensesInfo?.length}
                            partner={partnerDetails} 
                            updateLicenses={getAuraSupportInfo}
                        />

                        <div className="pt-3" style={{ paddingBottom: '60px' }}>
                            <AuraAppLicenses 
                                auraAppLicenses={filteredUserLicenses}
                                auraTotalAppLicensesCount= {nonSuiteLicensesInfo.length}
                                partner={partnerDetails} 
                                updateLicenses={getAuraSupportInfo}
                                otherLicenses={otherLicensesInfo}
                            />
                        </div>    
                    </div>
                </>
            }
        </>
    );
}

export default Licenses;